import { Tooltip } from '@ds';
import { useFormContext } from 'react-hook-form';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useLocationDateTime } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { useWatchFields } from '@/hooks/useWatchFields';

const INTERLINK_TIME_TOOLTIP_TEXT =
  'Departure field is pre-populated, and the Departure and Arrival fields are interlinked.';

const LegTimeLinkIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="30"
    height="100"
    viewBox="0 0 30 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M25 1H23C18.5817 1 15 4.58172 15 9V91C15 95.4183 18.5817 99 23 99H25" stroke="#C7C9D3" />
    <rect width="20" height="20" transform="translate(5 39.5)" fill="#F6F6FB" />
    <path
      d="M19.168 45.334H15.8346V47.0007H19.168C20.543 47.0007 21.668 48.1257 21.668 49.5007C21.668 50.8757 20.543 52.0007 19.168 52.0007H15.8346V53.6673H19.168C21.468 53.6673 23.3346 51.8007 23.3346 49.5007C23.3346 47.2007 21.468 45.334 19.168 45.334ZM14.168 52.0007H10.8346C9.45964 52.0007 8.33464 50.8757 8.33464 49.5007C8.33464 48.1257 9.45964 47.0007 10.8346 47.0007H14.168V45.334H10.8346C8.53464 45.334 6.66797 47.2007 6.66797 49.5007C6.66797 51.8007 8.53464 53.6673 10.8346 53.6673H14.168V52.0007ZM11.668 48.6673H18.3346V50.334H11.668V48.6673Z"
      fill="#595C6E"
    />
  </svg>
);

const LegTimeUnlinkIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="30"
    height="100"
    viewBox="0 0 30 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M25 1H23C18.5817 1 15 4.58172 15 9V91C15 95.4183 18.5817 99 23 99H25" stroke="#C7C9D3" />
    <rect width="20" height="20" transform="translate(5 39.5)" fill="#F6F6FB" />
    <path
      d="M16.993 48.6661L18.3346 50.0078V48.6661H16.993ZM19.168 45.3328H15.8346V46.9161H19.168C20.593 46.9161 21.7513 48.0745 21.7513 49.4995C21.7513 50.5578 21.1096 51.4745 20.193 51.8661L21.3596 53.0328C22.543 52.2995 23.3346 50.9911 23.3346 49.4995C23.3346 47.1995 21.468 45.3328 19.168 45.3328ZM6.66797 43.0578L9.25964 45.6495C7.74297 46.2661 6.66797 47.7578 6.66797 49.4995C6.66797 51.7995 8.53464 53.6661 10.8346 53.6661H14.168V52.0828H10.8346C9.40964 52.0828 8.2513 50.9245 8.2513 49.4995C8.2513 48.1745 9.25964 47.0828 10.5513 46.9411L12.2763 48.6661H11.668V50.3328H13.943L15.8346 52.2245V53.6661H17.2763L20.618 57.0078L21.793 55.8328L7.84297 41.8828L6.66797 43.0578Z"
      fill="#595C6E"
    />
  </svg>
);

type LegTimeLinkProps = {
  isDisable?: boolean;
  className?: string;
};

export const LegTimeLink = ({ isDisable = false, className }: LegTimeLinkProps) => {
  const { calculateLegTimeDuration, setLegDuration } = useLegFormContext();
  const { handleArrivalLocationDateTime } = useLocationDateTime();
  const { setValue } = useFormContext<CommonRouteLegSchemaValues>();
  const { formType, legType, isDateTimeLinked, arrivalLocation } = useWatchFields<CommonRouteLegSchemaValues>([
    'isDateTimeLinked',
    'arrivalLocation',
    'formType',
    'legType',
  ] as const);

  const handleDateTimeLinkChange = async () => {
    setValue('isDateTimeLinked', !isDateTimeLinked);

    if (!isDateTimeLinked) {
      const data = await calculateLegTimeDuration(legType);
      setLegDuration(formType, legType, data);
      handleArrivalLocationDateTime(arrivalLocation.timezone);
    }
  };

  return (
    <Tooltip isDisabled={isDisable} content={INTERLINK_TIME_TOOLTIP_TEXT} placement="top">
      <button disabled={isDisable} type="button" className={className} onClick={handleDateTimeLinkChange}>
        {isDateTimeLinked ? (
          <LegTimeLinkIcon className={isDisable ? 'opacity-40 grayscale' : ''} />
        ) : (
          <LegTimeUnlinkIcon className={isDisable ? 'opacity-40 grayscale' : ''} />
        )}
      </button>
    </Tooltip>
  );
};
